import { useState } from "react";
import { useShallow } from "zustand/shallow";
import { addToast, Alert, Button, NumberInput } from "@heroui/react";
import { TrashIcon } from "@phosphor-icons/react";

import { useBoundStore } from "@/app/store";
import { supabase } from "@/services/supabase/client";
import { DrawerAndModal } from "@/shared/components/common/DrawerAndModal";

interface DeleteAccountProps {
  id: string;
  externalState: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  };
}

// Generate a random 6-digit code
const generateVerificationCode = () => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

export function ManageDeleteAccount({ id, externalState }: DeleteAccountProps) {
  const { formValues, handleChange, resetForm, errors, validateForm, verificationCode } = useDeleteAccountForm();
  const [isDeleting, setIsDeleting] = useState(false);
  const clearUser = useBoundStore(useShallow((state) => state.user.clear));
  const { deviceType } = useBoundStore(useShallow((state) => ({ deviceType: state.device.type })));

  const handleOpenChangeWithReset = (open: boolean) => {
    if (!open && !isDeleting) {
      resetForm();
    }
    externalState.onOpenChange(open);
  };

  const handleDeleteAccount = async () => {
    if (!validateForm()) return;

    setIsDeleting(true);

    try {
      // Call the delete account edge function
      const { error } = await supabase.functions.invoke("delete-account");

      if (error) {
        throw error;
      }

      // Clear user state and sign out
      clearUser();
      await supabase.auth.signOut();

      addToast({
        title: "Account deleted successfully",
        description: "Your account and all associated data have been permanently deleted.",
        color: "success",
      });

      externalState.onOpenChange(false);
      resetForm();
    } catch (error) {
      console.error("Error deleting account:", error);
      addToast({
        title: "Error deleting account",
        description: "There was an error processing your account deletion. Please try again.",
        color: "danger",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    externalState.onOpenChange(false);
  };

  return (
    <DrawerAndModal
      id={id}
      icon={<TrashIcon />}
      title="Delete Account"
      closeLabel="Cancel"
      confirmLabel="Delete Account"
      onConfirm={handleDeleteAccount}
      externalState={{ isOpen: externalState.isOpen, onOpenChange: handleOpenChangeWithReset }}
    >
      <Alert
        color="danger"
        title="Warning: This action cannot be undone!"
        description="This will permanently delete all your data. Action cannot be undone."
      />

      <div className="space-y-2">
        <p className="text-default-600 text-sm">To confirm account deletion, please enter the confirmation code:</p>

        <p className="text-center font-mono font-bold tracking-widest">{verificationCode}</p>
      </div>

      <NumberInput
        autoComplete="off"
        errorMessage={errors.code}
        formatOptions={{ useGrouping: false }}
        hideStepper
        isDisabled={isDeleting}
        isInvalid={!!errors.code}
        label="Confirmation Code"
        name="code"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleDeleteAccount();
          }
        }}
        placeholder="Enter the 6-digit code"
        size="sm"
        type="number"
        value={formValues.code}
        onValueChange={handleChange}
      />
      {deviceType === "mobile" && (
        <div className="flex gap-2 pt-2">
          <Button variant="flat" fullWidth onPress={handleCancel}>
            Cancel
          </Button>

          <Button color="danger" isLoading={isDeleting} variant="flat" fullWidth onPress={handleDeleteAccount}>
            Delete
          </Button>
        </div>
      )}
    </DrawerAndModal>
  );
}

function useDeleteAccountForm() {
  const [verificationCode] = useState(() => generateVerificationCode());
  const [formValues, setFormValues] = useState<{ code: number | undefined }>({ code: undefined });
  const [errors, setErrors] = useState<{ code?: string }>({});

  const handleChange = (value: number | undefined) => {
    setFormValues((prev) => ({ ...prev, code: value }));
    setErrors((prev) => ({ ...prev, code: undefined })); // Clear error on change
  };

  const validateForm = () => {
    const newErrors: { code?: string } = {};

    if (formValues.code === undefined || formValues.code === 0) {
      newErrors.code = "Confirmation code is required.";
    } else if (formValues.code < 100000 || formValues.code > 999999) {
      newErrors.code = "Confirmation code must be 6 digits.";
    } else if (formValues.code.toString() !== verificationCode) {
      newErrors.code = "Confirmation code is incorrect.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormValues({ code: undefined });
    setErrors({});
  };

  return { formValues, handleChange, resetForm, errors, validateForm, verificationCode };
}
